import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Globe, Zap, Users } from 'lucide-react';
import { cn } from '../../lib/utils';
import AnimatedGraphics from './AnimatedGraphics';

interface HeroSectionProps {
  onGetStarted?: () => void;
  onLogin?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onGetStarted, onLogin }) => {
  const { t } = useTranslation('landing');

  return (
    <section className="relative pt-16 pb-20 overflow-hidden bg-gradient-to-br from-ink-50 via-ink-100 to-sand-100">
      {/* Animated Background Graphics */}
      <div className="absolute inset-0 opacity-30">
        <AnimatedGraphics />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-accent-500/10 text-accent-600 px-4 py-2 mb-6 border border-accent-500/20"
            >
              <Zap className="w-4 h-4" />
              <span className="text-sm font-medium tracking-wide">Early Warning • Early Action</span>
            </motion.div>

            {/* Main Title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="font-display font-bold text-ink-900 mb-6 leading-tight uppercase"
            >
              {t('hero.title')}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-lg md:text-xl text-ink-600 mb-8 leading-relaxed max-w-2xl"
            >
              {t('hero.subtitle')}
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-ink-500 mb-10 leading-relaxed max-w-2xl"
            >
              {t('hero.description')}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                onClick={() => {
                  const signupSection = document.querySelector('[data-signup-section]');
                  signupSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group bg-accent-500 hover:bg-accent-600 text-white px-8 py-4 font-medium transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>{t('hero.cta.primary').toUpperCase()}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>

              <motion.button
                onClick={() => {
                  const capabilitiesSection = document.querySelector('[data-capabilities-section]');
                  capabilitiesSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group bg-transparent border-2 border-ink-300 hover:border-accent-500 text-ink-700 hover:text-accent-600 px-8 py-4 font-medium transition-all duration-300 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span>{t('hero.cta.secondary').toUpperCase()}</span>
              </motion.button>
            </motion.div>

            {/* Development Status */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="flex items-center gap-6 mt-12 pt-8 border-t border-ink-200"
            >
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                <span className="text-ink-600 text-sm font-medium uppercase">IN DEVELOPMENT</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-ocean-500" />
                <span className="text-ink-600 text-sm uppercase">COMING SOON: OPEN SOURCE</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Visual Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            {/* Main Visual Container */}
            <div className="relative bg-white p-8 shadow-2xl border border-ink-100">
              {/* Concept Visualization */}
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="h-2 bg-accent-500 w-32 mx-auto mb-2"></div>
                  <div className="text-sm font-mono text-ink-500 uppercase tracking-wider">CLEAR SYSTEM</div>
                </div>
                
                {/* System Flow */}
                <div className="space-y-4">
                  {['Data Sources', 'AI Detection', 'Field Interface', 'Rapid Response'].map((label, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.8 + i * 0.3 }}
                      className="flex items-center gap-4"
                    >
                      <div className={cn(
                        "w-3 h-3 rounded-full",
                        i === 0 && "bg-sage-500",
                        i === 1 && "bg-accent-500", 
                        i === 2 && "bg-ocean-500",
                        i === 3 && "bg-ink-600"
                      )}></div>
                      <div className="flex-1">
                        <div className="text-xs font-mono text-ink-500 mb-1">{label}</div>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: '100%' }}
                          transition={{ duration: 1, delay: 1 + i * 0.2 }}
                          className={cn(
                            "h-2",
                            i === 0 && "bg-sage-200",
                            i === 1 && "bg-accent-200",
                            i === 2 && "bg-ocean-200", 
                            i === 3 && "bg-ink-200"
                          )}
                        ></motion.div>
                      </div>
                      {i < 3 && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 1.5 + i * 0.2 }}
                          className="text-ink-300 text-xs"
                        >
                          →
                        </motion.div>
                      )}
                    </motion.div>
                  ))}
                </div>
                
                {/* Status Indicator */}
                <div className="text-center pt-6 border-t border-ink-100">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 2.5 }}
                    className="inline-flex items-center gap-2 bg-yellow-50 text-yellow-700 px-4 py-2 border border-yellow-200"
                  >
                    <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-mono uppercase">IN DEVELOPMENT</span>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute -top-4 -right-4 bg-accent-500 text-white p-4 shadow-xl"
            >
              <Zap className="w-6 h-6" />
            </motion.div>

            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 4, repeat: Infinity, delay: 1 }}
              className="absolute -bottom-6 -left-6 bg-ocean-500 text-white p-3 shadow-xl"
            >
              <Globe className="w-5 h-5" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-ink-300 flex justify-center p-1"
        >
          <div className="w-1 h-3 bg-ink-400"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;